import React, { useMemo } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import api from 'lib/trpcClient';

type Props = {
  handleChangeCallback: (value: string | undefined) => void;
  firmId?: string;
  value?: string;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  currentShareholdingsOnly?: boolean;
};

const DistinctLegalCompanyNameSelect: React.FC<Props> = ({
  handleChangeCallback,
  firmId,
  value,
  error,
  helperText,
  disabled,
  currentShareholdingsOnly = false,
}) => {
  const { data: uniqueCompaniesByLegalName } =
    api.investee.listUniqueInvestees.useQuery(
      {
        firmId: firmId ?? '',
        currentShareholdingsOnly,
      },
      {
        enabled: !!firmId,
        staleTime: 0,
        refetchOnWindowFocus: true,
      },
    );

  const companyOptions = useMemo(() => {
    if (!uniqueCompaniesByLegalName?.result) return [];

    return uniqueCompaniesByLegalName.result.map((investee) => ({
      id: investee._id,
      label: `${investee.tradingName} (${investee.legalName})`,
    }));
  }, [uniqueCompaniesByLegalName]);

  const selectedCompany = companyOptions.find(
    (company) => company.id === value,
  );

  return (
    <Autocomplete
      disabled={!companyOptions.length || disabled}
      options={companyOptions}
      getOptionLabel={(option) => option.label || ''}
      getOptionKey={(option) => option.id}
      filterSelectedOptions
      value={selectedCompany ?? null}
      onChange={(_event, selectedOption) => {
        handleChangeCallback(selectedOption?.id);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          placeholder="Select company"
          error={error}
          helperText={helperText}
          name="companyId"
        />
      )}
    />
  );
};

export default DistinctLegalCompanyNameSelect;
